import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDebounce } from 'use-debounce';
import './_styles_/AuctionCreateTab.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_AUCTIONS_QUERY_NAME, CREATE_AUCTION } from '../../graphql/auctions';
import { useExtendedState } from '../../hooks/useExtendedState';
import TextField from '../fields/TextField';
import NumberField from '../fields/NumberField';
import SelectField from '../fields/SelectField';
import DateTimeField from '../fields/DateTimeField';
import CheckboxField from '../fields/CheckboxField';
import SaveButton from '../buttons/SaveButton';
import { auctionPropertiesType } from '../../types';
import {
    AUCTION_DESCRIPTION_MAX_LENGTH,
    AUCTION_NAME_LENGTH,
    auctionAbsoluteDeviationValidator,
    auctionAuctioneerDataItemsValidator,
    auctionDateValidator,
    auctionDeliveryYearValidator,
    auctionDescriptionValidator,
    auctionMaxBidSizeValidator,
    auctionMaxPriceValidator,
    auctionMinPriceValidator,
    auctionNameValidator,
    auctionPowerValidator,
    auctionQuantityValueValidator,
    auctionRelativeDeviationValidator,
    auctionSmallestQuantityUnitValidator,
    auctionSpotReferencePriceValidator,
} from '../../validators/auctions';
import useMultiValidator from '../../hooks/useMultiValidator';
import { date, getCurrentYear } from '../../utils';
import Constants from '../../utils/Constants';
import Unit from '../Unit';
import AuctionEnergyPerLot from './energy/AuctionEnergyPerLot';
import ExpandedListTab from '../ExpandedListTab';
import { BUY, getAuctionType, SELL } from '../../utils/AuctionTypes';
import { getBiddingType, OPEN, SLOTTED } from '../../utils/BiddingTypes';
import { BID_PRICE, getTradePriceCalculationType } from '../../utils/TradePriceCalculationTypes';
import { getAuctionAlgorithm } from '../../utils/AuctionAlgorithms';
import { AUTO, getAuctionAcceptanceType } from '../../utils/AuctionAcceptanceTypes';
import { getSettlementStartType } from '../../utils/SettlementStartTypes';
import { ENERGY, getProductType, SECURITY } from '../../utils/ProductTypes';
import AuctioneerDataMatrix from '../auctioneer-data-matrix/AuctioneerDataMatrix';
import useSingleValidator from '../../hooks/useSingleValidator';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';
import { computeHighestAcceptablePrice, computeLowestAcceptablePrice } from '../../utils/auctions';
import {
    AUCTION_BLOCKER_MISSING_PRODUCT,
    AUCTION_BLOCKER_MISSING_TECHNICAL_AUCTIONEER,
    AUCTION_BLOCKER_MISSING_ZONE,
    transformAuctionBlockers,
} from '../blockers/AuctionBlockers';
import InfoPanel from '../InfoPanel';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

export const changeTimeOfDatetime = (oldDate, timeString) => date(oldDate, (d) => {
    d.setHours(parseInt(timeString.substring(0, 2), 10));
    d.setMinutes(parseInt(timeString.substring(3, 5), 10));
    return d;
});

AuctionCreateTab.propTypes = {
    hideHandler: PropTypes.func.isRequired,
    auctionProps: auctionPropertiesType.isRequired,
};

function AuctionCreateTab(props) { // NOSONAR javascript:S3776
    const {
        hideHandler,
        auctionProps,
    } = props;
    const {
        availableProducts,
        availableZones,
        availableAuctioneers,
        availableAuctionTypes,
        availableBiddingTypes,
        availableTradePriceCalculationTypes,
        availableAuctionAlgorithms,
        availableAuctionAcceptanceTypes,
        availableSettlStartTypes,
        availableCountries,
        availableAuctioneerDataTemplates,
        defaultAuctionDurationHours,
        availableAuctionTemplates,
    } = auctionProps;
    const { t } = useTranslation();
    const creationBlockers = [];
    if (!availableProducts || availableProducts.length === 0) {
        creationBlockers.push(AUCTION_BLOCKER_MISSING_PRODUCT);
    }
    if (!availableZones || availableZones.length === 0) {
        creationBlockers.push(AUCTION_BLOCKER_MISSING_ZONE);
    }
    const availableRegularAuctioneers = availableAuctioneers?.filter((availableAuctioneer) => availableAuctioneer.regularAuctioneer);
    const availableTechnicalAuctioneers = availableAuctioneers?.filter((availableAuctioneer) => availableAuctioneer.technicalAuctioneer);
    if (!availableTechnicalAuctioneers || availableTechnicalAuctioneers.length === 0) {
        creationBlockers.push(AUCTION_BLOCKER_MISSING_TECHNICAL_AUCTIONEER);
    }
    const [defaultOpeningDate] = useState(date(null, (newDate) => newDate.setSeconds(0, 0)));
    const [defaultClosingDate] = useState(date(defaultOpeningDate, (newDate) => newDate.setHours(newDate.getHours() + defaultAuctionDurationHours)));
    const templateIdToApplyState = useExtendedState('');
    const nameState = useExtendedState('');
    const deliveryYearState = useExtendedState(getCurrentYear().toString());
    const productState = useExtendedState(availableProducts?.length ? availableProducts[0] : null);
    const productTypeState = useExtendedState(getProductType(productState.value?.productType));
    const zoneState = useExtendedState(availableZones?.length ? availableZones[0] : null);
    const [currencyPostfix, setCurrencyPostfix] = useState(zoneState.value && <>&nbsp;{zoneState.value.currency.code}</>);
    const auctioneerState = useExtendedState(availableTechnicalAuctioneers?.length ? availableTechnicalAuctioneers[0] : null);
    const auctionTypeState = useExtendedState(availableAuctionTypes[0]);
    const quantityValueState = useExtendedState(1);
    const powerState = useExtendedState('1');
    const openingTimeState = useExtendedState(defaultOpeningDate);
    const closingTimeState = useExtendedState(defaultClosingDate);
    const showLastPriceState = useExtendedState(false);
    const showRankingState = useExtendedState(false);
    const limitBidModificationState = useExtendedState(false);
    const minPriceState = useExtendedState(null);
    const maxPriceState = useExtendedState(null);
    const spotReferencePriceState = useExtendedState(null);
    const absoluteDeviationState = useExtendedState(null);
    const relativeDeviationState = useExtendedState(null);
    const biddingTypeState = useExtendedState(getBiddingType(availableBiddingTypes[0]));
    const tradePriceCalculationTypeState = useExtendedState(getTradePriceCalculationType(availableTradePriceCalculationTypes[0]));
    const acceptanceTypeState = useExtendedState(availableAuctionAcceptanceTypes[0]);
    const algorithmState = useExtendedState(availableAuctionAlgorithms[0]);
    const smallestQuantityUnitState = useExtendedState(Constants.MIN_SMALLEST_UNIT);
    const maxBidSizeState = useExtendedState(null);
    const settlementStartTypeState = useExtendedState(availableSettlStartTypes[0]);
    const fastMarketState = useExtendedState(false);
    const auctioneerDataItemsState = useExtendedState([]);
    const roundUpHighestAcceptablePriceState = useExtendedState(false);
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(auctionNameValidator(t), wafValidator(t)),
        deliveryYear: auctionDeliveryYearValidator(t),
        quantityValue: compositeValidator(auctionQuantityValueValidator(t, productTypeState.value), wafValidator(t)),
        power: compositeValidator(auctionPowerValidator(t), wafValidator(t)),
        openingTime: auctionDateValidator(t),
        closingTime: auctionDateValidator(t),
        description: compositeValidator(auctionDescriptionValidator(t), wafValidator(t)),
        spotReferencePrice: auctionSpotReferencePriceValidator(t, Constants.MIN_SPOT_REFERENCE_PRICE, Constants.MAX_SPOT_REFERENCE_PRICE),
        absoluteDeviation: auctionAbsoluteDeviationValidator(t, Constants.MIN_ABSOLUTE_DEVIATION, Constants.MAX_ABSOLUTE_DEVIATION),
        relativeDeviation: auctionRelativeDeviationValidator(t, Constants.MIN_RELATIVE_DEVIATION, Constants.MAX_RELATIVE_DEVIATION),
    }, { name: false });
    const {
        validator: aucioneerDataItemsValidator,
        isValid: isAuctioneerDataItemsValid,
        reset: resetAuctioneerDataItemsValidator,
    } = useSingleValidator(false, useMemo(() => auctionAuctioneerDataItemsValidator(t, quantityValueState.value), [t, quantityValueState.value]));
    const { validator: minPriceValidator, isValid: isMinPriceValid } = useSingleValidator(true, useMemo(() => compositeValidator(auctionMinPriceValidator(t, maxPriceState.value), wafValidator(t)), [t, maxPriceState.value]));
    const { validator: maxPriceValidator, isValid: isMaxPriceValid } = useSingleValidator(true, useMemo(() => compositeValidator(auctionMaxPriceValidator(t, minPriceState.value), wafValidator(t)), [t, minPriceState.value]));
    const {
        validator: smallestQuantityUnitValidator,
        isValid: isSmallestQuantityUnitValid,
        reset: resetSmallestQuantityUnitValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionSmallestQuantityUnitValidator(
        t,
        Constants.MIN_SMALLEST_UNIT,
        quantityValueState.value,
        maxBidSizeState.value),
    wafValidator(t)), [t, quantityValueState.value, maxBidSizeState.value]));
    const {
        validator: maxBidSizeValidator,
        isValid: isMaxBidSizeValid,
        reset: resetMaxBidSizeValidator,
    } = useSingleValidator(true, useMemo(() => compositeValidator(auctionMaxBidSizeValidator(
        t,
        Constants.MIN_MAXIMUM_BID_SIZE,
        smallestQuantityUnitState.value,
        quantityValueState.value),
    wafValidator(t)), [t, smallestQuantityUnitState.value, quantityValueState.value]));
    const descriptionState = useExtendedState(null);
    const [debouncedDeliveryYear] = useDebounce(deliveryYearState.value, Constants.ENERGY_PER_LOT_PROPERTY_DEBOUNCE_IN_MS);
    const [debouncedPower] = useDebounce(powerState.value, Constants.ENERGY_PER_LOT_PROPERTY_DEBOUNCE_IN_MS);
    const handleNewOpeningTime = (newOpeningTime) => {
        const oldOpeningTime = openingTimeState.value;
        if (oldOpeningTime !== newOpeningTime) {
            openingTimeState.setValue(newOpeningTime);
            if (newOpeningTime instanceof Date && oldOpeningTime instanceof Date && closingTimeState.value instanceof Date) {
                const newClosingTime = date(newOpeningTime, (d) => {
                    const hoursDiff = Math.abs(closingTimeState.value - oldOpeningTime) / 36e5;
                    d.setHours(d.getHours() + hoursDiff);
                });
                closingTimeState.setValue(newClosingTime);
            }
        }
    };
    const keyToProduct = useCallback((key) => availableProducts.find((p) => p.productId === key), [availableProducts]);
    const onProductSelect = (newProduct) => {
        const newProductType = getProductType(newProduct.productType);
        if (!newProductType.is(productTypeState.value)) {
            deliveryYearState.reset();
            quantityValueState.reset();
            powerState.reset();
            settlementStartTypeState.reset();
            fastMarketState.reset();
        }
        productTypeState.setValue(getProductType(newProduct.productType));
    };
    const onProductReset = () => {
        productTypeState.reset();
        productState.reset();
    };
    const keyToZone = useCallback((key) => availableZones.find((z) => z.code === key), [availableZones]);
    const onZoneSelect = (newZone) => {
        setCurrencyPostfix(<>&nbsp;{newZone.currency.code}</>);
    };
    const keyToAuctioneer = useCallback((key) => availableTechnicalAuctioneers.find((a) => a.memberId === key), [availableTechnicalAuctioneers]);
    const onAuctioneerSelect = () => {
        resetAuctioneerDataItemsValidator();
        auctioneerDataItemsState.reset();
    };
    const keyToBiddingType = ((key) => getBiddingType(key));
    const onBiddingTypeSelect = (newValue) => {
        if (newValue.serverType !== biddingTypeState.value.serverType) {
            showRankingState.reset();
            limitBidModificationState.reset();
            smallestQuantityUnitState.reset();
            resetSmallestQuantityUnitValidator();
            maxBidSizeState.reset();
            resetMaxBidSizeValidator();
        }
    };
    const keyToTradePriceCalculationType = ((key) => getTradePriceCalculationType(key));
    const onTradePriceCalculationTypeSelect = (newValue) => {
        if (newValue.serverType !== tradePriceCalculationTypeState.value.serverType) {
            showLastPriceState.reset();
        }
    };

    const [isDisabled, setIsDisabled] = useState(true);
    let initialValidation = Promise.resolve();
    if (creationBlockers.length > 0) {
        initialValidation = Promise.reject();
    } else if (auctioneerState?.value?.euAuctioneer) {
        initialValidation = isAuctioneerDataItemsValid();
    }

    Promise.all([initialValidation, isValid(), isMinPriceValid(), isMaxPriceValid(), isSmallestQuantityUnitValid(), isMaxBidSizeValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const variables = {
        name: nameState.value,
        deliveryYear: productTypeState.value.is(ENERGY) ? parseInt(deliveryYearState.value, 10) : null,
        productId: productState.value ? productState.value.productId : null,
        quantity: quantityValueState.value,
        power: productTypeState.value.is(ENERGY) ? parseInt(powerState.value, 10) : null,
        openingTime: openingTimeState.value,
        closureTime: closingTimeState.value,
        showLastPrice: tradePriceCalculationTypeState.value.is(BID_PRICE) ? showLastPriceState.value : null,
        showRanking: biddingTypeState.value.is(SLOTTED) ? showRankingState.value : null,
        limitBidModification: biddingTypeState.value.is(SLOTTED) ? limitBidModificationState.value : null,
        minPrice: minPriceState.value,
        maxPrice: maxPriceState.value,
        description: descriptionState.value,
        spotReferencePrice: spotReferencePriceState.value,
        relativeDeviation: relativeDeviationState.value,
        absoluteDeviation: absoluteDeviationState.value,
        roundUpHighestAcceptablePrice: roundUpHighestAcceptablePriceState.value,
        zoneCode: zoneState.value ? zoneState.value.code : null,
        auctioneerMemberId: auctioneerState.value ? auctioneerState.value.memberId : null,
        auctionType: auctionTypeState.value,
        biddingType: biddingTypeState.value.serverType,
        tradePriceCalculationType: tradePriceCalculationTypeState.value.serverType,
        auctionAlgorithm: algorithmState.value,
        auctionAcceptanceType: acceptanceTypeState.value,
        smallestUnit: biddingTypeState.value.is(OPEN) ? smallestQuantityUnitState.value : null,
        maxBidSize: biddingTypeState.value.is(OPEN) ? maxBidSizeState.value : null,
        settlStartType: productTypeState.value.is(SECURITY) ? settlementStartTypeState.value : null,
        fastMarket: productTypeState.value.is(SECURITY) ? fastMarketState.value : null,
        auctioneerDataItems: auctioneerState.value && auctioneerState.value.euAuctioneer ? auctioneerDataItemsState.value.map((item) => ({
            memberId: item.auctioneer.memberId,
            countryCode: item.country.code,
            quantity: item.quantity,
        })) : null,
    };
    const extendedMutation = useExtendedMutation(CREATE_AUCTION, {
        variables,
        successCallback: (data) => {
            hideHandler(data);
        },
        successMessageOptions: { key: 'auctions.notification.auction-create-success', dataProperty: 'createAuction' },
        failureMessageOptions: { key: 'auctions.notification.auction-create-failure', params: { name: nameState.value } },
        refetchQueries: [ALL_AUCTIONS_QUERY_NAME],
    });
    const lowestAcceptablePrice = computeLowestAcceptablePrice(
        spotReferencePriceState.value,
        relativeDeviationState.value,
        absoluteDeviationState.value,
    );
    const highestAcceptablePrice = computeHighestAcceptablePrice(
        spotReferencePriceState.value,
        relativeDeviationState.value,
        absoluteDeviationState.value,
        roundUpHighestAcceptablePriceState.value,
    );
    const highestAcceptablePriceVisible = ((getAuctionType(auctionTypeState.value).is(BUY) && getAuctionAcceptanceType(acceptanceTypeState.value).is(AUTO)) || getAuctionType(auctionTypeState.value).is(SELL)) && highestAcceptablePrice !== null;
    const isApplyTemplateDisabled = !templateIdToApplyState.value;
    const applyTemplate = () => {
        const templateIdToApplyInt = parseInt(templateIdToApplyState.value, 10);
        const auctionTemplate = availableAuctionTemplates.find((availableTemplate) => availableTemplate.id === templateIdToApplyInt);
        nameState.setValue(auctionTemplate.name);
        if (auctionTemplate.product) {
            productTypeState.setValue(getProductType(auctionTemplate.product.productType));
            productState.setValue(keyToProduct(auctionTemplate.product.productId));
        } else {
            onProductReset();
        }
        if (auctionTemplate.tradePriceCalculationType) {
            tradePriceCalculationTypeState.setValue(getTradePriceCalculationType(auctionTemplate.tradePriceCalculationType));
        } else {
            tradePriceCalculationTypeState.reset();
        }
        if (auctionTemplate.zone) {
            zoneState.setValue(keyToZone(auctionTemplate.zone.code));
            setCurrencyPostfix(<>&nbsp;{auctionTemplate.zone.currency.code}</>);
        } else {
            zoneState.reset();
        }
        if (auctionTemplate.auctioneerMember) {
            auctioneerState.setValue(keyToAuctioneer(auctionTemplate.auctioneerMember.memberId));
        } else {
            auctioneerState.reset();
        }
        if (auctionTemplate.biddingType) {
            biddingTypeState.setValue(getBiddingType(auctionTemplate.biddingType));
        } else {
            biddingTypeState.reset();
        }
        if (auctionTemplate.openingTime) {
            openingTimeState.setValue(changeTimeOfDatetime(openingTimeState.value, auctionTemplate.openingTime));
        } else {
            openingTimeState.reset();
        }
        if (auctionTemplate.closureTime) {
            closingTimeState.setValue(changeTimeOfDatetime(closingTimeState.value, auctionTemplate.closureTime));
        } else if (auctionTemplate.openingTime) {
            closingTimeState.setValue(date(changeTimeOfDatetime(openingTimeState.value, auctionTemplate.openingTime), (newDate) => newDate.setHours(newDate.getHours() + defaultAuctionDurationHours)));
        } else {
            closingTimeState.reset();
        }
        if (auctionTemplate.algorithm) {
            algorithmState.setValue(auctionTemplate.algorithm);
        } else {
            algorithmState.reset();
        }
        if (auctionTemplate.auctionType) {
            auctionTypeState.setValue(auctionTemplate.auctionType);
        } else {
            auctionTypeState.reset();
        }
        if (auctionTemplate.auctionAcceptanceType) {
            acceptanceTypeState.setValue(auctionTemplate.auctionAcceptanceType);
        } else {
            acceptanceTypeState.reset();
        }
        if (auctionTemplate.settlStartType) {
            settlementStartTypeState.setValue(auctionTemplate.settlStartType);
        } else {
            settlementStartTypeState.reset();
        }
        descriptionState.setValue(auctionTemplate.description);
        if (auctionTemplate.showLastPrice) {
            showLastPriceState.setValue(auctionTemplate.showLastPrice);
        } else {
            showLastPriceState.reset();
        }
        if (auctionTemplate.showRanking) {
            showRankingState.setValue(auctionTemplate.showRanking);
        } else {
            showRankingState.reset();
        }
        if (auctionTemplate.limitBidModification) {
            limitBidModificationState.setValue(auctionTemplate.limitBidModification);
        } else {
            limitBidModificationState.reset();
        }
        minPriceState.setValue(auctionTemplate.minPrice);
        maxPriceState.setValue(auctionTemplate.maxPrice);
        spotReferencePriceState.setValue(auctionTemplate.spotReferencePrice);
        absoluteDeviationState.setValue(auctionTemplate.absoluteDeviation);
        relativeDeviationState.setValue(auctionTemplate.relativeDeviation);
        quantityValueState.setValue(auctionTemplate.quantity);
        deliveryYearState.setValue(auctionTemplate.deliveryYear);
        powerState.setValue(auctionTemplate.power);
        smallestQuantityUnitState.setValue(auctionTemplate.smallestUnit);
        maxBidSizeState.setValue(auctionTemplate.maxBidSize);
        templateIdToApplyState.reset();
    };

    return (
        <ExpandedListTab
            className="auction-create-tab"
        >
            <div className="rich-list-row-expanded--fields">
                {availableAuctionTemplates?.length > 0 && (
                    <>
                        <h5>{t('auctions.auction-setup.apply-auction-template')}</h5>
                        <div className="apply-auction-template">
                            <SelectField
                                label={t('auctions.auction-setup.auction-template')}
                                state={templateIdToApplyState}
                                className="auction-create-tab--fields--auction-template"
                                inputId="auction-create-tab--fields--auction-template"
                                emptyOption={<option value="">...</option>}
                                options={availableAuctionTemplates.map((template) => <option value={template.id} key={template.id}>{template.name}</option>)}
                            />
                            <button
                                type="button"
                                className="apply-auction-template-button button button-bordered"
                                disabled={isApplyTemplateDisabled}
                                onClick={applyTemplate}
                            >
                                <FontAwesomeIcon icon="check" />&nbsp;{t('auctions.auction-setup.button.apply-auction-template')}
                            </button>
                        </div>
                    </>
                )}
                <h5>{t('auctions.auction-setup.auction-details')}</h5>
                <div className="auction-setup--details">
                    <TextField
                        label={t('auctions.auction-setup.name')}
                        state={nameState}
                        className="auction-create-tab--fields--name wide"
                        inputId="auction-create-tab--fields--name"
                        validator={validators.name}
                        maxLength={AUCTION_NAME_LENGTH}
                        placeholder={t('auctions.placeholder.name')}
                        isRequired
                    />
                    {productState.value && (
                        <SelectField
                            label={t('auctions.auction-setup.product')}
                            onChange={onProductSelect}
                            className="auction-create-tab--fields--product"
                            inputId="auction-create-tab--fields--product"
                            value={productState.value.productId}
                            state={productState}
                            valueTransformer={keyToProduct}
                            options={availableProducts.map((p) => <option value={p.productId} key={p.productId}>{p.name} ({p.productId})</option>)}
                            onReset={onProductReset}
                            isRequired
                        />
                    )}
                    {availableZones?.length > 1 && (
                        <SelectField
                            label={t('auctions.auction-setup.zone')}
                            onChange={onZoneSelect}
                            className="auction-create-tab--fields--zone"
                            inputId="auction-create-tab--fields--zone"
                            state={zoneState}
                            value={zoneState.value.code}
                            valueTransformer={keyToZone}
                            options={availableZones.map((z) => <option value={z.code} key={z.code}>{z.name} ({z.code})</option>)}
                            isRequired
                        />
                    )}
                    {availableTechnicalAuctioneers?.length > 1 && (
                        <SelectField
                            label={t('auctions.auction-setup.auctioneer')}
                            onChange={onAuctioneerSelect}
                            className="auction-create-tab--fields--auctioneer"
                            inputId="auction-create-tab--fields--auctioneer"
                            state={auctioneerState}
                            valueTransformer={keyToAuctioneer}
                            value={auctioneerState.value.memberId}
                            options={availableTechnicalAuctioneers.map((a) => <option value={a.memberId} key={a.memberId}>{a.name} ({a.memberId})</option>)}
                            isRequired
                        />
                    )}
                    <DateTimeField
                        label={t('auctions.auction-setup.opening')}
                        state={openingTimeState}
                        onChange={handleNewOpeningTime}
                        className="auction-create-tab--fields--opening-time wide"
                        inputId="auction-create-tab--fields--opening-time"
                        invalidBefore={date()}
                        validator={validators.openingTime}
                        isRequired
                    />
                    <DateTimeField
                        label={t('auctions.auction-setup.closing')}
                        state={closingTimeState}
                        className="auction-create-tab--fields--closing-time wide"
                        inputId="auction-create-tab--fields--closing-time"
                        invalidBefore={openingTimeState.value}
                        validator={validators.closingTime}
                        isRequired
                    />
                    {productTypeState.value.is(ENERGY) && (
                        <>
                            <NumberField
                                label={t('auctions.auction-setup.delivery-year')}
                                className="auction-create-tab--fields--delivery-year"
                                inputId="auction-create-tab--fields--delivery-year"
                                state={deliveryYearState}
                                min={getCurrentYear()}
                                validator={validators.deliveryYear}
                                isRequired
                                noThousandSeparator
                            />
                            <NumberField
                                label={t('auctions.auction-setup.number-of-lots')}
                                className="auction-create-tab--fields--quantity"
                                inputId="auction-create-tab--fields--quantity"
                                state={quantityValueState}
                                validator={validators.quantityValue}
                                min={1}
                                step={Constants.DEFAULT_QUANTITY_STEP}
                                isRequired
                            />
                            <NumberField
                                label={t('auctions.auction-setup.power')}
                                className="auction-create-tab--fields--power"
                                inputId="auction-create-tab--fields--power"
                                state={powerState}
                                validator={validators.power}
                                postfix={<Unit unit={productState.value.quantityUnit.substring(0, productState.value.quantityUnit.length - 1)} />}
                                min={1}
                                isRequired
                            />
                            <AuctionEnergyPerLot
                                label={t('auctions.auction-setup.energy-per-lot')}
                                deliveryYear={debouncedDeliveryYear ? String(debouncedDeliveryYear) : ''}
                                productId={productState.value.productId}
                                quantityUnit={<Unit unit={productState.value.quantityUnit} />}
                                power={debouncedPower ? String(debouncedPower) : ''}
                                className="auction-create-tab--fields--energy-per-lot"
                            />
                        </>
                    )}
                    {productTypeState.value.is(SECURITY) && (
                        <NumberField
                            label={t('auctions.auction-setup.quantity')}
                            className="auction-create-tab--fields--quantity"
                            inputId="auction-create-tab--fields--quantity"
                            state={quantityValueState}
                            validator={validators.quantityValue}
                            min={1}
                            step={Constants.DEFAULT_QUANTITY_STEP}
                            isRequired
                        />
                    )}
                </div>
                <h5>{t('auctions.auction-setup.auction-configuration')}</h5>
                <div className="auction-configuration">
                    {productTypeState.value.is(ENERGY) && (
                        <CheckboxField
                            label={t('auctions.auction-setup.round-up-highest-acceptable-price')}
                            state={roundUpHighestAcceptablePriceState}
                            className="auction-create-tab--fields--round-up-highest-acceptable-price"
                            inputId="auction-create-tab--fields--round-up-highest-acceptable-price"
                        />
                    )}
                    {availableAuctionTypes.length > 1 && (
                        <SelectField
                            label={t('auctions.auction-setup.auction-type')}
                            state={auctionTypeState}
                            className="auction-create-tab--fields--auction-type"
                            inputId="auction-create-tab--fields--auction-type"
                            options={availableAuctionTypes.map((availableAuctionType) => (
                                <option value={availableAuctionType} key={availableAuctionType}>
                                    {getAuctionType(availableAuctionType).translate(t)}
                                </option>
                            ))}
                            isRequired
                        />
                    )}
                    {availableBiddingTypes.length > 1 && (
                        <SelectField
                            label={t('auctions.auction-setup.bidding-type')}
                            onChange={onBiddingTypeSelect}
                            className="auction-create-tab--fields--bidding-type"
                            inputId="auction-create-tab--fields--bidding-type"
                            state={biddingTypeState}
                            value={biddingTypeState.value.serverType}
                            valueTransformer={keyToBiddingType}
                            options={availableBiddingTypes.map((availableBiddingType) => (
                                <option value={availableBiddingType} key={availableBiddingType}>
                                    {getBiddingType(availableBiddingType).translate(t)}
                                </option>
                            ))}
                            isRequired
                        />
                    )}
                    {availableTradePriceCalculationTypes.length > 1 && (
                        <SelectField
                            label={t('auctions.auction-setup.trade-price-calculation-type')}
                            onChange={onTradePriceCalculationTypeSelect}
                            className="auction-create-tab--fields--trade-price-calculation-type"
                            inputId="auction-create-tab--fields--trade-price-calculation-type"
                            state={tradePriceCalculationTypeState}
                            value={tradePriceCalculationTypeState.value.serverType}
                            valueTransformer={keyToTradePriceCalculationType}
                            options={availableTradePriceCalculationTypes.map((availableTradePriceCalculationType) => (
                                <option value={availableTradePriceCalculationType} key={availableTradePriceCalculationType}>
                                    {getTradePriceCalculationType(availableTradePriceCalculationType).translate(t)}
                                </option>
                            ))}
                            isRequired
                        />
                    )}
                    {availableAuctionAlgorithms.length > 1 && (
                        <SelectField
                            label={t('auctions.auction-setup.algorithm')}
                            state={algorithmState}
                            className="auction-create-tab--fields--algorithm"
                            inputId="auction-create-tab--fields--algorithm"
                            options={availableAuctionAlgorithms.map((availableAuctionAlgorithm) => (
                                <option value={availableAuctionAlgorithm} key={availableAuctionAlgorithm}>
                                    {getAuctionAlgorithm(availableAuctionAlgorithm).translate(t)}
                                </option>
                            ))}
                            isRequired
                        />
                    )}
                    {availableAuctionAcceptanceTypes.length > 1 && (
                        <SelectField
                            label={t('auctions.auction-setup.acceptance-type')}
                            state={acceptanceTypeState}
                            className="auction-create-tab--fields--acceptance-type"
                            inputId="auction-create-tab--fields--acceptance-type"
                            options={availableAuctionAcceptanceTypes.map((availableAcceptanceType) => (
                                <option value={availableAcceptanceType} key={availableAcceptanceType}>
                                    {getAuctionAcceptanceType(availableAcceptanceType).translate(t)}
                                </option>
                            ))}
                            isRequired
                        />
                    )}
                    {tradePriceCalculationTypeState.value.is(BID_PRICE) && (
                        <CheckboxField
                            label={t('auctions.auction-setup.show-last-price')}
                            state={showLastPriceState}
                            className="auction-create-tab--fields--show-last-price"
                            inputId="auction-create-tab--fields--show-last-price"
                        />
                    )}
                    {biddingTypeState.value.is(SLOTTED) && (
                        <>
                            <CheckboxField
                                label={t('auctions.auction-setup.show-ranking')}
                                state={showRankingState}
                                className="auction-create-tab--fields--show-ranking"
                                inputId="auction-create-tab--fields--show-ranking"
                            />
                            <CheckboxField
                                label={t('auctions.auction-setup.limit-bid-modification')}
                                state={limitBidModificationState}
                                className="auction-create-tab--fields--limit-bid-modification"
                                inputId="auction-create-tab--fields--limit-bid-modification"
                            />
                        </>
                    )}
                    <NumberField
                        label={t('auctions.auction-setup.min-price')}
                        className="auction-create-tab--fields--min-price"
                        inputId="auction-create-tab--fields--min-price"
                        state={minPriceState}
                        max={maxPriceState.value}
                        fractionDigits={2}
                        validator={minPriceValidator}
                        postfix={currencyPostfix}
                        step={Constants.DEFAULT_PRICE_STEP}
                        validateOnValidatorChange
                    />
                    <NumberField
                        label={t('auctions.auction-setup.max-price')}
                        className="auction-create-tab--fields--max-price"
                        inputId="auction-create-tab--fields--max-price"
                        state={maxPriceState}
                        min={minPriceState.value}
                        fractionDigits={2}
                        validator={maxPriceValidator}
                        postfix={currencyPostfix}
                        step={Constants.DEFAULT_PRICE_STEP}
                        validateOnValidatorChange
                    />
                    <NumberField
                        label={t('auctions.auction-setup.spot-reference-price')}
                        className="auction-create-tab--fields--spot-reference-price"
                        inputId="auction-create-tab--fields--spot-reference-price"
                        state={spotReferencePriceState}
                        min={Constants.MIN_SPOT_REFERENCE_PRICE}
                        max={Constants.MAX_SPOT_REFERENCE_PRICE}
                        fractionDigits={2}
                        validator={validators.spotReferencePrice}
                        postfix={currencyPostfix}
                        step={Constants.DEFAULT_PRICE_STEP}
                    />
                    <NumberField
                        label={t('auctions.auction-setup.abs-deviation-limit')}
                        className="auction-create-tab--fields--absolute-deviation"
                        inputId="auction-create-tab--fields--absolute-deviation"
                        state={absoluteDeviationState}
                        min={Constants.MIN_ABSOLUTE_DEVIATION}
                        max={Constants.MAX_ABSOLUTE_DEVIATION}
                        fractionDigits={2}
                        validator={validators.absoluteDeviation}
                        postfix={currencyPostfix}
                        step={Constants.DEFAULT_PRICE_STEP}
                    />
                    <NumberField
                        label={t('auctions.auction-setup.rel-deviation-limit')}
                        className="auction-create-tab--fields--relative-deviation"
                        inputId="auction-create-tab--fields--relative-deviation"
                        state={relativeDeviationState}
                        min={Constants.MIN_RELATIVE_DEVIATION}
                        max={Constants.MAX_RELATIVE_DEVIATION}
                        fractionDigits={2}
                        validator={validators.relativeDeviation}
                        postfix={<>&nbsp;%</>}
                        step={Constants.DEFAULT_PRICE_STEP}
                    />
                    {(getAuctionType(auctionTypeState.value).is(BUY) || (getAuctionType(auctionTypeState.value).is(SELL) && getAuctionAcceptanceType(acceptanceTypeState.value).is(AUTO))) && lowestAcceptablePrice !== null && (
                        <ReadonlyNumberField
                            label={t('auctions.auction-setup.lowest-acceptable-price')}
                            value={lowestAcceptablePrice}
                            fractionDigits={2}
                            className="auction-create-tab--fields--lowest-acceptable-price"
                            postfix={currencyPostfix}
                        />
                    )}
                    {highestAcceptablePriceVisible && (
                        <ReadonlyNumberField
                            label={t('auctions.auction-setup.highest-acceptable-price')}
                            value={highestAcceptablePrice}
                            fractionDigits={2}
                            className="auction-create-tab--fields--highest-acceptable-price"
                            postfix={currencyPostfix}
                        />
                    )}
                    {productTypeState.value.is(SECURITY) && (
                        <>
                            <SelectField
                                label={t('auctions.auction-setup.settlement-start')}
                                state={settlementStartTypeState}
                                className="auction-create-tab--fields--settlement-start"
                                inputId="auction-create-tab--fields--settlement-start"
                                options={availableSettlStartTypes.map((availableSettlementStartType) => (
                                    <option value={availableSettlementStartType} key={availableSettlementStartType}>
                                        {getSettlementStartType(availableSettlementStartType).translate(t)}
                                    </option>
                                ))}
                                isRequired
                            />
                            <CheckboxField
                                label={t('auctions.auction-setup.fast-market')}
                                state={fastMarketState}
                                className="auction-create-tab--fields--fast-market"
                                inputId="auction-create-tab--fields--fast-market"
                            />
                        </>
                    )}
                    {biddingTypeState.value.is(OPEN) && (
                        <>
                            <NumberField
                                label={t('auctions.auction-setup.smallest-unit')}
                                className="auction-create-tab--fields--smallest-quantity-unit"
                                inputId="auction-create-tab--fields--smallest-quantity-unit"
                                state={smallestQuantityUnitState}
                                min={Constants.MIN_SMALLEST_UNIT}
                                max={quantityValueState.value}
                                validator={smallestQuantityUnitValidator}
                                step={Constants.DEFAULT_QUANTITY_STEP}
                                isRequired
                                validateOnValidatorChange
                            />
                            <NumberField
                                label={t('auctions.auction-setup.maximum-bid-size')}
                                className="auction-create-tab--fields--max-bid-size"
                                inputId="auction-create-tab--fields--max-bid-size"
                                state={maxBidSizeState}
                                min={Constants.MIN_MAXIMUM_BID_SIZE}
                                max={quantityValueState.value}
                                validator={maxBidSizeValidator}
                                step={smallestQuantityUnitState.value}
                                validateOnValidatorChange
                            />
                        </>
                    )}
                    <TextField
                        label={t('auctions.auction-setup.description')}
                        state={descriptionState}
                        className="auction-create-tab--fields--description x-wide"
                        inputId="auction-create-tab--fields--description"
                        validator={validators.description}
                        maxLength={AUCTION_DESCRIPTION_MAX_LENGTH}
                        placeholder={t('auctions.placeholder.description')}
                    />
                    {auctioneerState.value && auctioneerState.value.euAuctioneer && (
                        <div className="auction-create-tab--fields--auctioneer-data-matrix">
                            <AuctioneerDataMatrix
                                availableAuctioneers={availableRegularAuctioneers}
                                availableCountries={availableCountries}
                                availableTemplates={availableAuctioneerDataTemplates}
                                maxQuantitySum={quantityValueState.value}
                                items={auctioneerDataItemsState.value}
                                validator={aucioneerDataItemsValidator}
                                onItemsChange={auctioneerDataItemsState.setValue}
                                onReset={auctioneerDataItemsState.reset}
                                isChanged={auctioneerDataItemsState.isChanged}
                                isRequired
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton disabled={isDisabled} extendedMutation={extendedMutation} confirmationMessage={t('auctions.confirm-create-auction', { name: nameState.value })} />
            </div>
            {creationBlockers.map((creationBlocker) => (
                <InfoPanel
                    key={creationBlocker}
                    message={transformAuctionBlockers(t, creationBlocker)}
                />
            ))}
        </ExpandedListTab>
    );
}

export default AuctionCreateTab;
